import { ShieldCheck } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { formatRelativeTime } from '../../utils/format';
import type { WazuhAgent } from '../../types';

const statusTone: Record<WazuhAgent['status'], 'success' | 'danger' | 'warning' | 'neutral'> = {
  active: 'success',
  disconnected: 'danger',
  pending: 'warning',
  never_connected: 'neutral',
};

const statusLabel: Record<WazuhAgent['status'], string> = {
  active: 'Active',
  disconnected: 'Disconnected',
  pending: 'Pending',
  never_connected: 'Never connected',
};

export function AgentTable({ agents, loading }: { agents: WazuhAgent[]; loading?: boolean }) {
  if (!loading && agents.length === 0) {
    return <EmptyState icon={ShieldCheck} title="No agents need attention" description="Everything reporting in is healthy." />;
  }

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-800">
            <th className="py-2.5 pr-4 font-medium">Agent</th>
            <th className="py-2.5 pr-4 font-medium">Status</th>
            <th className="py-2.5 pr-4 font-medium">IP</th>
            <th className="py-2.5 pr-4 font-medium">OS</th>
            <th className="py-2.5 pr-4 font-medium">Last seen</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5} className="py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                  </td>
                </tr>
              ))
            : agents.map((agent) => (
                <tr key={agent.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3 pr-4 font-medium text-slate-800 dark:text-slate-200">{agent.name}</td>
                  <td className="py-3 pr-4">
                    <Badge tone={statusTone[agent.status]}>{statusLabel[agent.status]}</Badge>
                  </td>
                  <td className="py-3 pr-4 text-slate-500 dark:text-slate-400">{agent.ip ?? '-'}</td>
                  <td className="py-3 pr-4 text-slate-500 dark:text-slate-400">{agent.osName ?? 'Unknown'}</td>
                  <td className="py-3 pr-4 text-slate-400">
                    {agent.lastKeepAlive ? formatRelativeTime(agent.lastKeepAlive) : 'Never'}
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
